import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import type {
  ConversationListContactStateName,
  ConversationListSortName,
  ConversationListStatusName,
  ConversationListViewName,
  ConversationTimelineEventDto,
  ContactPlatformProfileSummaryDto,
  ConversationDto,
  ConversationListItemDto,
  CreateTemplateMessageParameter,
  MessageDto,
  PlatformTypeName,
} from "@multi-chat/shared";
import { addPlatformProfileAsContact } from "../../api/contacts-api.js";
import { isApiError } from "../../api/client.js";
import i18n from "../../i18n/index.js";
import { EVENTS_COALESCE_MS } from "../../api/inbox-events-api.js";
import {
  VISIBILITY_RECONCILE_COALESCE_MS,
  shouldReconcileInboxOnVisibility,
  shouldRefreshListAfterEventsOpen,
  shouldRefreshMessagesAfterEventsOpen,
} from "./inbox-realtime-recovery.js";
import {
  CONVERSATION_PAGE_SIZE,
  MESSAGE_PAGE_SIZE,
  createConversationMessage,
  createConversationTemplateMessage,
  getConversation,
  listConversationMessages,
  listConversations,
  markConversationRead,
  markConversationUnread,
  updateConversationAssignment,
  updateConversationStatus,
} from "../../api/inbox-api.js";
import { normalizeConversationSearch } from "../../api/conversation-list-query.js";
import {
  applyConversationDetailsMutationOverlay,
  applyConversationMutationOverlays,
  conversationListEmptyKind,
  conversationListFetchMode,
  conversationListQueryKey,
  conversationMatchesActiveList,
  conversationSnapshotFromDetails,
  isStaleConversationListResponse,
  nextSelectedConversationSnapshot,
  patchSelectedSnapshotFromDetails,
  patchSelectedSnapshotProfile,
  patchSelectedSnapshotUnread,
  reconcileReadSuccessInList,
  resolveSelectedConversation,
  shouldRescopeInbox,
  withDeepLinkedConversation,
  type ConversationMutationOverlay,
} from "./conversation-list-state.js";
import { isFreeformOutboundAllowed } from "./freeform-outbound-eligibility.js";
import { mapOutboundSendError } from "./outbound-media-errors.js";
import {
  appendConversations,
  chronologicalMessages,
  mergeMessageFirstPage,
  newestInboundMessageIdFromNewestPage,
  prependOlderMessages,
  reconcileConversationFirstPage,
  retainedTailConversationIds,
} from "./inbox-display.js";
import {
  INBOX_EVENTS_COALESCE_MAX_MS,
  planInboxEventCoalesce,
} from "./inbox-event-coalesce.js";
import { quotedMessageSeekPlan } from "./conversation-message-anchor.js";
import { shouldReassertDeepLinkedSelection } from "./inbox-conversation-deep-link.js";
import { useInboxRealtime } from "./InboxRealtimeProvider.js";
import { inboxWorkflowErrorMessage } from "./inbox-workflow-errors.js";
import { mergeTimelineEvents } from "./merge-conversation-timeline.js";
import { usePendingAttachmentReconciliation } from "./use-pending-attachment-reconciliation.js";

export type InboxListQuery = {
  search?: string;
  platform?: PlatformTypeName;
  unread?: boolean;
  contactState?: ConversationListContactStateName;
  sort?: ConversationListSortName;
  view?: ConversationListViewName;
  status?: ConversationListStatusName;
};

export type InboxListView =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "searchEmpty"; query: string }
  | { kind: "filterEmpty" }
  | { kind: "searchFilterEmpty"; query: string }
  | { kind: "ready"; items: ConversationListItemDto[]; nextCursor: string | null }
  | { kind: "error"; message: string };

export type InboxMessagesView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "ready"; items: MessageDto[] }
  | { kind: "error"; message: string };

export type ContactActionStatus =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string; code?: string };

export type SaveContactInput =
  | {
      mode: "CREATE_NEW";
      firstName?: string;
      lastName?: string;
      displayName?: string;
      email?: string;
      phone?: string;
      phoneCountryIso?: string;
      birthday?: string;
      groupIds?: number[];
      newGroupNames?: string[];
    }
  | {
      mode: "LINK_EXISTING";
      contactId: number;
      confirmWhatsAppPhoneReplacement?: boolean;
    };

function hasSaveContactIdentity(input: SaveContactInput): boolean {
  if (input.mode === "LINK_EXISTING") {
    return input.contactId > 0;
  }
  return [input.displayName, input.firstName, input.lastName, input.email, input.phone].some(
    (value) => (value?.trim().length ?? 0) > 0,
  );
}

export type SendMessageStatus =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string; code?: string };

interface UseInboxResult {
  listView: InboxListView;
  messagesView: InboxMessagesView;
  /** Persisted ConversationEvent rows for the selected thread (from messages pages). */
  conversationEvents: ConversationTimelineEventDto[];
  selectedConversationId: number | null;
  selectedConversation: ConversationListItemDto | null;
  /** Details DTO fields needed by the composer (CS window). */
  conversationDetails: ConversationDto | null;
  contactActionStatus: ContactActionStatus;
  sendStatus: SendMessageStatus;
  hasMoreConversations: boolean;
  loadingMoreConversations: boolean;
  listRefreshing: boolean;
  hasOlderMessages: boolean;
  loadingOlderMessages: boolean;
  selectConversation: (conversationId: number) => void;
  openConversationById: (conversationId: number) => Promise<{ ok: boolean }>;
  loadMoreConversations: () => boolean;
  loadOlderMessages: () => boolean;
  ensureQuotedMessageLoaded: (messageId: string) => Promise<boolean>;
  refreshList: () => void;
  refreshMessages: () => void;
  saveContact: (input: SaveContactInput) => Promise<{ ok: boolean; code?: string }>;
  restoreContact: () => Promise<{ ok: boolean; code?: string }>;
  clearContactActionError: () => void;
  clearSendError: () => void;
  sendMessage: (
    content: string,
    options?: { replyToMessageId?: string; attachmentId?: string },
  ) => Promise<boolean>;
  sendTemplateMessage: (input: {
    whatsappTemplateId: number;
    parameters: CreateTemplateMessageParameter[];
    clientIdempotencyKey: string;
  }) => Promise<boolean>;
  workflowPending: boolean;
  assignConversation: (conversationId: number, assignedMemberId: number | null) => Promise<{ ok: boolean; message?: string }>;
  setConversationStatus: (conversationId: number, status: "OPEN" | "CLOSED") => Promise<{ ok: boolean; message?: string }>;
  markConversationUnread: (conversationId: number) => Promise<{ ok: boolean; message?: string }>;
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (error.status === 0) return fallback;
    if (error.code === "ORGANIZATION_SUSPENDED") {
      return i18n.t("organization.suspended.body");
    }
    return error.message;
  }
  return fallback;
}

function toSendError(error: unknown, fallback: string): Extract<SendMessageStatus, { kind: "error" }> {
  const message = mapOutboundSendError(i18n.t.bind(i18n), error, fallback);
  if (isApiError(error)) {
    if (error.status === 0) {
      return { kind: "error", message };
    }
    return { kind: "error", message, code: error.code };
  }
  return { kind: "error", message };
}

function patchConversationProfile(
  items: ConversationListItemDto[],
  conversationId: number,
  contactPlatformProfile: ContactPlatformProfileSummaryDto,
): ConversationListItemDto[] {
  return items.map((item) =>
    item.conversationId === conversationId
      ? { ...item, contactPlatformProfile }
      : item,
  );
}

export function useInbox(
  organizationId: number,
  unreachableMessage: string,
  listQuery: InboxListQuery = {},
  inboxScopeVersion = 0,
  viewerMemberId: number | null = null,
): UseInboxResult {
  const [listView, setListView] = useState<InboxListView>({ kind: "loading" });
  const [messagesView, setMessagesView] = useState<InboxMessagesView>({ kind: "idle" });
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [conversationDetails, setConversationDetails] = useState<ConversationDto | null>(null);
  const [listReloadToken, setListReloadToken] = useState(0);
  const [messagesReloadToken, setMessagesReloadToken] = useState(0);
  const [listNextCursor, setListNextCursor] = useState<string | null>(null);
  const [messagesNextCursor, setMessagesNextCursor] = useState<string | null>(null);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [contactActionStatus, setContactActionStatus] = useState<ContactActionStatus>({
    kind: "idle",
  });
  const [sendStatus, setSendStatus] = useState<SendMessageStatus>({ kind: "idle" });
  const [selectedSnapshot, setSelectedSnapshot] = useState<ConversationListItemDto | null>(
    null,
  );
  const [listRefreshing, setListRefreshing] = useState(false);
  const [workflowPending, setWorkflowPending] = useState(false);
  const [conversationEvents, setConversationEvents] = useState<ConversationTimelineEventDto[]>(
    [],
  );
  const { subscribe: subscribeInboxRealtime, subscribeReconnect } = useInboxRealtime();
  const search = normalizeConversationSearch(listQuery.search);
  const platform = listQuery.platform;
  const unread = listQuery.unread === true;
  const contactState = listQuery.contactState;
  const sort = listQuery.sort;
  const view = listQuery.view;
  const listStatus = listQuery.status;
  const viewRef = useRef(view);
  viewRef.current = view;
  const listStatusRef = useRef(listStatus);
  listStatusRef.current = listStatus;
  const viewerMemberIdRef = useRef(viewerMemberId);
  viewerMemberIdRef.current = viewerMemberId;
  const suppressMarkReadRef = useRef<number | null>(null);

  const unreadFilterActiveRef = useRef(unread);
  unreadFilterActiveRef.current = unread;
  const lastUnreadMutationReadConversationIdRef = useRef<number | null>(null);
  if (!unread) {
    lastUnreadMutationReadConversationIdRef.current = null;
  }

  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;
  // Localized copy changes with the language; read it without refetching the list or thread.
  const unreachableMessageRef = useRef(unreachableMessage);
  unreachableMessageRef.current = unreachableMessage;
  const selectedConversationIdRef = useRef<number | null>(null);
  selectedConversationIdRef.current = selectedConversationId;
  const selectedSnapshotRef = useRef<ConversationListItemDto | null>(null);
  selectedSnapshotRef.current = selectedSnapshot;
  const listViewRef = useRef(listView);
  listViewRef.current = listView;
  const messagesViewRef = useRef(messagesView);
  messagesViewRef.current = messagesView;
  const listNextCursorRef = useRef(listNextCursor);
  listNextCursorRef.current = listNextCursor;
  const messagesNextCursorRef = useRef(messagesNextCursor);
  messagesNextCursorRef.current = messagesNextCursor;
  const contactActionInFlightRef = useRef(false);
  const sendInFlightRef = useRef(false);
  const listLoadMoreInFlightRef = useRef(false);
  const messagesLoadOlderInFlightRef = useRef(false);
  const messagesFetchInFlightRef = useRef(false);
  const listLoadMoreAbortRef = useRef<AbortController | null>(null);
  const messagesLoadOlderAbortRef = useRef<AbortController | null>(null);
  const listLoadedQueryKeyRef = useRef<string | null>(null);
  const listQueryKeyRef = useRef<string | null>(null);
  const listInitialLoadQueryKeyRef = useRef<string | null>(null);
  const messagesLoadedKeyRef = useRef<string | null>(null);
  const lastMarkReadAttemptRef = useRef<string | null>(null);
  const mutationEpochRef = useRef(0);
  const mutationOverlaysRef = useRef(new Map<number, ConversationMutationOverlay>());
  const changedConversationIdsRef = useRef(new Set<number>());
  const pendingTailCheckRef = useRef<{
    orgId: number;
    view: ConversationListViewName;
    targets: number[];
  } | null>(null);
  const messagesGenerationRef = useRef(0);

  const onOrganizationChange = useEffectEvent(() => {
    listLoadMoreAbortRef.current?.abort();
    listLoadMoreAbortRef.current = null;
    messagesLoadOlderAbortRef.current?.abort();
    messagesLoadOlderAbortRef.current = null;
    listLoadMoreInFlightRef.current = false;
    messagesLoadOlderInFlightRef.current = false;
    listLoadedQueryKeyRef.current = null;
    messagesLoadedKeyRef.current = null;
    lastMarkReadAttemptRef.current = null;
    mutationOverlaysRef.current.clear();
    setSelectedConversationId(null);
    setSelectedSnapshot(null);
    setListView({ kind: "loading" });
    setMessagesView({ kind: "idle" });
    setConversationDetails(null);
    setConversationEvents([]);
    setListNextCursor(null);
    setMessagesNextCursor(null);
    lastUnreadMutationReadConversationIdRef.current = null;
    setLoadingMoreConversations(false);
    setListRefreshing(false);
    setLoadingOlderMessages(false);
    setContactActionStatus({ kind: "idle" });
    setSendStatus({ kind: "idle" });
  });

  useEffect(() => {
    onOrganizationChange();
  }, [organizationId]);

  const onInboxScopeChange = useEffectEvent(() => {
    listLoadMoreAbortRef.current?.abort();
    listLoadMoreAbortRef.current = null;
    messagesLoadOlderAbortRef.current?.abort();
    messagesLoadOlderAbortRef.current = null;
    listLoadMoreInFlightRef.current = false;
    messagesLoadOlderInFlightRef.current = false;
    listLoadedQueryKeyRef.current = null;
    messagesLoadedKeyRef.current = null;
    lastMarkReadAttemptRef.current = null;
    mutationOverlaysRef.current.clear();
    setSelectedConversationId(null);
    setSelectedSnapshot(null);
    setListView({ kind: "loading" });
    setMessagesView({ kind: "idle" });
    setConversationDetails(null);
    setConversationEvents([]);
    setListNextCursor(null);
    setMessagesNextCursor(null);
    lastUnreadMutationReadConversationIdRef.current = null;
    setLoadingMoreConversations(false);
    setListRefreshing(false);
    setLoadingOlderMessages(false);
    setContactActionStatus({ kind: "idle" });
    setSendStatus({ kind: "idle" });
  });

  const observedScopeVersionRef = useRef(inboxScopeVersion);
  useEffect(() => {
    if (!shouldRescopeInbox(observedScopeVersionRef.current, inboxScopeVersion)) return;
    observedScopeVersionRef.current = inboxScopeVersion;
    onInboxScopeChange();
    setListReloadToken((token) => token + 1);
  }, [inboxScopeVersion]);

  const selectedConversation = resolveSelectedConversation(
    selectedConversationId,
    listView.kind === "ready" ? listView.items : undefined,
    selectedSnapshot,
  );

  const refreshList = useCallback(() => setListReloadToken((token) => token + 1), []);
  const refreshMessages = useCallback(() => setMessagesReloadToken((token) => token + 1), []);

  const selectConversation = useCallback((conversationId: number) => {
    if (selectedConversationIdRef.current === conversationId) {
      return;
    }
    suppressMarkReadRef.current = null;
    messagesLoadOlderAbortRef.current?.abort();
    messagesLoadOlderAbortRef.current = null;
    messagesLoadOlderInFlightRef.current = false;
    messagesLoadedKeyRef.current = null;
    lastMarkReadAttemptRef.current = null;
    setSelectedConversationId(conversationId);
    const currentItems = listViewRef.current.kind === "ready" ? listViewRef.current.items : [];
    const selectedRow = currentItems.find((item) => item.conversationId === conversationId);
    if (selectedRow) {
      setSelectedSnapshot(selectedRow);
    } else {
      setSelectedSnapshot(null);
    }
    setMessagesView({ kind: "loading" });
    setConversationDetails(null);
    setMessagesNextCursor(null);
    setLoadingOlderMessages(false);
    setContactActionStatus({ kind: "idle" });
    setSendStatus({ kind: "idle" });
  }, []);

  const openConversationById = useCallback(
    async (conversationId: number): Promise<{ ok: boolean }> => {
      if (!Number.isInteger(conversationId) || conversationId <= 0) {
        return { ok: false };
      }
      const requestOrgId = orgIdRef.current;
      selectConversation(conversationId);
      const currentItems =
        listViewRef.current.kind === "ready" ? listViewRef.current.items : [];
      if (currentItems.some((item) => item.conversationId === conversationId)) {
        return { ok: true };
      }
      try {
        const details = await getConversation(requestOrgId, conversationId);
        // Selection may have been cleared during the async gap (Strict Mode effect
        // re-entry, inboxScopeVersion rescope). Re-assert only if still same org.
        if (orgIdRef.current !== requestOrgId) {
          return { ok: false };
        }
        if (
          !shouldReassertDeepLinkedSelection({
            requestOrganizationId: requestOrgId,
            currentOrganizationId: orgIdRef.current,
            currentSelectedConversationId: selectedConversationIdRef.current,
            conversationId,
          })
        ) {
          return { ok: true };
        }
        const snapshot = conversationSnapshotFromDetails(details);
        setSelectedConversationId(conversationId);
        setSelectedSnapshot(snapshot);
        setListView((current) => {
          if (current.kind !== "ready") {
            return current;
          }
          if (current.items.some((item) => item.conversationId === conversationId)) {
            return current;
          }
          return {
            kind: "ready",
            items: [snapshot, ...current.items],
            nextCursor: current.nextCursor,
          };
        });
        return { ok: true };
      } catch {
        if (selectedConversationIdRef.current === conversationId) {
          setSelectedConversationId(null);
          setSelectedSnapshot(null);
          setMessagesView({ kind: "idle" });
        }
        return { ok: false };
      }
    },
    [selectConversation],
  );

  const markSelectedReadFromNewestPage = useEffectEvent(
    async (
      currentOrgId: number,
      conversationId: number,
      newestFirstPage: MessageDto[],
    ): Promise<void> => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      if (suppressMarkReadRef.current === conversationId) return;
      if (orgIdRef.current !== currentOrgId) return;
      if (selectedConversationIdRef.current !== conversationId) return;

      const throughMessageId = newestInboundMessageIdFromNewestPage(newestFirstPage);
      if (!throughMessageId) return;

      const attemptKey = `${currentOrgId}:${conversationId}:${throughMessageId}`;
      if (lastMarkReadAttemptRef.current === attemptKey) return;
      lastMarkReadAttemptRef.current = attemptKey;

      try {
        const response = await markConversationRead(currentOrgId, conversationId, {
          throughMessageId,
        });
        if (orgIdRef.current !== currentOrgId) return;
        if (selectedConversationIdRef.current !== conversationId) return;

        mutationEpochRef.current += 1;
        mutationOverlaysRef.current.set(conversationId, {
          epoch: mutationEpochRef.current,
          unreadCount: response.unreadCount,
        });

        setListView((current) => {
          if (current.kind !== "ready") return current;
          const items = reconcileReadSuccessInList(
            current.items,
            conversationId,
            response.unreadCount,
            unreadFilterActiveRef.current,
          );
          if (unreadFilterActiveRef.current && response.unreadCount === 0) {
            // Post-success reconciliation for `unread=true`:
            // once mark-read succeeds, the conversation must disappear from the unread-only list immediately,
            // while the selected snapshot keeps the thread/details open.
            lastUnreadMutationReadConversationIdRef.current = conversationId;
          }
          return {
            kind: "ready",
            items,
            nextCursor: current.nextCursor,
          };
        });
        setSelectedSnapshot((current) =>
          patchSelectedSnapshotUnread(current, conversationId, response.unreadCount),
        );
      } catch {
        if (lastMarkReadAttemptRef.current === attemptKey) {
          lastMarkReadAttemptRef.current = null;
        }
      }
    },
  );

  const clearContactActionError = useCallback(() => {
    setContactActionStatus({ kind: "idle" });
  }, []);

  const clearSendError = useCallback(() => {
    setSendStatus({ kind: "idle" });
  }, []);

  const applyProfilePatch = useCallback(
    (conversationId: number, contactPlatformProfile: ContactPlatformProfileSummaryDto) => {
      setListView((current) => {
        if (current.kind !== "ready") return current;
        return {
          kind: "ready",
          items: patchConversationProfile(current.items, conversationId, contactPlatformProfile),
          nextCursor: current.nextCursor,
        };
      });
      setSelectedSnapshot((current) =>
        patchSelectedSnapshotProfile(current, conversationId, contactPlatformProfile),
      );
      setConversationDetails((current) =>
        current && current.conversationId === conversationId
          ? { ...current, contactPlatformProfile }
          : current,
      );
      setContactActionStatus({ kind: "idle" });
    },
    [],
  );

  const saveContact = useCallback(
    async (input: SaveContactInput): Promise<{ ok: boolean; code?: string }> => {
      const conversation = selectedConversation;
      if (!conversation || contactActionInFlightRef.current) return { ok: false };

      if (!hasSaveContactIdentity(input)) return { ok: false };

      contactActionInFlightRef.current = true;
      setContactActionStatus({ kind: "submitting" });
      try {
        const response = await addPlatformProfileAsContact(
          orgIdRef.current,
          conversation.contactPlatformProfile.id,
          input.mode === "LINK_EXISTING"
            ? {
                mode: "LINK_EXISTING",
                contactId: input.contactId,
                confirmWhatsAppPhoneReplacement: input.confirmWhatsAppPhoneReplacement,
              }
            : {
                mode: "CREATE_NEW",
                firstName: input.firstName?.trim() || undefined,
                lastName: input.lastName?.trim() || undefined,
                displayName: input.displayName?.trim() || undefined,
                email: input.email?.trim() || undefined,
                phone: input.phone?.trim() || undefined,
                phoneCountryIso: input.phoneCountryIso,
                birthday: input.birthday || undefined,
                groupIds: input.groupIds,
                newGroupNames: input.newGroupNames,
              },
        );
        applyProfilePatch(conversation.conversationId, response.contactPlatformProfile);
        return { ok: true };
      } catch (error) {
        const code = isApiError(error) ? error.code : undefined;
        setContactActionStatus({
          kind: "error",
          message: toErrorMessage(error, unreachableMessage),
          code,
        });
        return { ok: false, code };
      } finally {
        contactActionInFlightRef.current = false;
      }
    },
    [applyProfilePatch, selectedConversation, unreachableMessage],
  );

  const restoreContact = useCallback(async (): Promise<{ ok: boolean; code?: string }> => {
    const conversation = selectedConversation;
    if (!conversation || contactActionInFlightRef.current) return { ok: false };

    contactActionInFlightRef.current = true;
    setContactActionStatus({ kind: "submitting" });
    try {
      const response = await addPlatformProfileAsContact(
        orgIdRef.current,
        conversation.contactPlatformProfile.id,
        { mode: "RESTORE_EXISTING" },
      );
      applyProfilePatch(conversation.conversationId, response.contactPlatformProfile);
      return { ok: true };
    } catch (error) {
      const code = isApiError(error) ? error.code : undefined;
      setContactActionStatus({
        kind: "error",
        message: toErrorMessage(error, unreachableMessage),
        code,
      });
      return { ok: false, code };
    } finally {
      contactActionInFlightRef.current = false;
    }
  }, [applyProfilePatch, selectedConversation, unreachableMessage]);

  const appendOutboundMessage = useCallback((message: MessageDto) => {
    setMessagesView((current) => {
      if (current.kind === "ready") {
        const withoutDuplicate = current.items.filter(
          (item) => item.messageId !== message.messageId,
        );
        return { kind: "ready", items: [...withoutDuplicate, message] };
      }
      return { kind: "ready", items: [message] };
    });
  }, []);

  const sendMessage = useCallback(
    async (
      content: string,
      options?: { replyToMessageId?: string; attachmentId?: string },
    ): Promise<boolean> => {
      const conversationId = selectedConversationId;
      if (conversationId == null || sendInFlightRef.current) return false;

      const trimmed = content.trim();
      const attachmentId = options?.attachmentId;
      if (trimmed.length === 0 && !attachmentId) return false;

      if (
        !isFreeformOutboundAllowed({
          platformType: conversationDetails?.platformType,
          canSendFreeformText: conversationDetails?.canSendFreeformText,
          customerServiceWindowExpiresAt:
            conversationDetails?.customerServiceWindowExpiresAt ?? null,
          nowMs: Date.now(),
        })
      ) {
        return false;
      }

      sendInFlightRef.current = true;
      setSendStatus({ kind: "submitting" });
      try {
        const message = await createConversationMessage(orgIdRef.current, conversationId, {
          clientIdempotencyKey: crypto.randomUUID(),
          ...(trimmed.length > 0 ? { content: trimmed } : {}),
          ...(attachmentId ? { attachmentIds: [attachmentId] } : {}),
          ...(options?.replyToMessageId
            ? { replyToMessageId: options.replyToMessageId }
            : {}),
        });
        appendOutboundMessage(message);
        setSendStatus({ kind: "idle" });
        return true;
      } catch (error) {
        setSendStatus(toSendError(error, i18n.t("inbox.composer.sendErrors.generic")));
        return false;
      } finally {
        sendInFlightRef.current = false;
      }
    },
    [
      appendOutboundMessage,
      conversationDetails?.canSendFreeformText,
      conversationDetails?.customerServiceWindowExpiresAt,
      conversationDetails?.platformType,
      selectedConversationId,
      unreachableMessage,
    ],
  );

  const sendTemplateMessage = useCallback(
    async (input: {
      whatsappTemplateId: number;
      parameters: CreateTemplateMessageParameter[];
      clientIdempotencyKey: string;
    }): Promise<boolean> => {
      const conversationId = selectedConversationId;
      if (conversationId == null || sendInFlightRef.current) return false;

      sendInFlightRef.current = true;
      setSendStatus({ kind: "submitting" });
      try {
        const message = await createConversationTemplateMessage(
          orgIdRef.current,
          conversationId,
          {
            clientIdempotencyKey: input.clientIdempotencyKey,
            whatsappTemplateId: input.whatsappTemplateId,
            parameters: input.parameters,
          },
        );
        appendOutboundMessage(message);
        setSendStatus({ kind: "idle" });
        return true;
      } catch (error) {
        setSendStatus(toSendError(error, i18n.t("inbox.composer.sendErrors.generic")));
        return false;
      } finally {
        sendInFlightRef.current = false;
      }
    },
    [appendOutboundMessage, selectedConversationId, unreachableMessage],
  );

  const dropTailRowsOutsideView = useEffectEvent(
    async (
      boundOrgId: number,
      activeView: NonNullable<InboxListQuery["view"]> | "ALL",
      targets: readonly number[],
    ): Promise<void> => {
      if (activeView === "ALL" || targets.length === 0) return;
      await Promise.all(
        targets.map(async (conversationId) => {
          try {
            const details = await getConversation(boundOrgId, conversationId);
            if (orgIdRef.current !== boundOrgId) return;
            if (
              conversationMatchesActiveList(
                details,
                activeView,
                listStatusRef.current ?? "ALL",
                viewerMemberIdRef.current,
              )
            ) {
              return;
            }
            setListView((current) => {
              if (current.kind !== "ready") return current;
              const items = current.items.filter((item) => item.conversationId !== conversationId);
              if (items.length === current.items.length) return current;
              return { kind: "ready", items, nextCursor: current.nextCursor };
            });
          } catch {
            // Keep the row when its detail cannot be loaded.
          }
        }),
      );
    },
  );

  useEffect(() => {
    const cursor = listView.kind === "ready" ? listView.nextCursor : null;
    listNextCursorRef.current = cursor;
    setListNextCursor((current) => (current === cursor ? current : cursor));
  }, [listView]);

  useEffect(() => {
    if (listView.kind !== "ready") return;
    const items = listView.items;
    setSelectedSnapshot((current) =>
      nextSelectedConversationSnapshot(selectedConversationIdRef.current, items, current),
    );
    const pending = pendingTailCheckRef.current;
    if (pending == null || pending.targets.length === 0) return;
    pendingTailCheckRef.current = null;
    void dropTailRowsOutsideView(pending.orgId, pending.view, pending.targets);
  }, [listView]);

  useEffect(() => {
    const controller = new AbortController();
    const currentOrgId = organizationId;
    const currentQuery = { search, platform, unread, contactState, sort, view, status: listStatus };
    const queryKey = conversationListQueryKey(currentOrgId, currentQuery);
    const previous = listViewRef.current;
    const mode = conversationListFetchMode({
      previousKey: listLoadedQueryKeyRef.current,
      nextKey: queryKey,
      previousKind: previous.kind,
    });
    listQueryKeyRef.current = queryKey;
    listInitialLoadQueryKeyRef.current = mode === "load" ? queryKey : null;
    const requestEpoch = mutationEpochRef.current;

    if (mode !== "merge") {
      listLoadMoreAbortRef.current?.abort();
      listLoadMoreAbortRef.current = null;
      listLoadMoreInFlightRef.current = false;
      setLoadingMoreConversations(false);
      setListNextCursor(null);
    }

    if (mode === "load") {
      setListRefreshing(false);
      setListView({ kind: "loading" });
    } else if (mode === "refresh") {
      setListRefreshing(true);
    }

    void (async () => {
      try {
        const response = await listConversations(currentOrgId, {
          limit: CONVERSATION_PAGE_SIZE,
          search,
          platform,
          unread,
          contactState,
          sort,
          view,
          status: listStatus,
          signal: controller.signal,
        });
        if (
          isStaleConversationListResponse({
            aborted: controller.signal.aborted || orgIdRef.current !== currentOrgId,
            requestKey: queryKey,
            currentKey: listQueryKeyRef.current ?? queryKey,
          })
        ) {
          return;
        }

        let itemsForQuery = response.items;
        // Prevent a stale list response (started before a successful mark-read)
        // from resurrecting the now-read conversation in the unread-only results.
        if (
          unreadFilterActiveRef.current &&
          lastUnreadMutationReadConversationIdRef.current != null
        ) {
          const excludeId = lastUnreadMutationReadConversationIdRef.current;
          itemsForQuery = itemsForQuery.filter((item) => item.conversationId !== excludeId);
          lastUnreadMutationReadConversationIdRef.current = null;
        }

        const changedIds = [...changedConversationIdsRef.current];
        const activeView = view ?? "ALL";

        setListView((current) => {
          const base = mode === "merge" && current.kind === "ready" ? current.items : [];
          const previousCursor =
            current.kind === "ready" ? current.nextCursor : listNextCursorRef.current;
          const reconciled =
            mode === "merge"
              ? reconcileConversationFirstPage({
                  previous: base,
                  firstPage: itemsForQuery,
                  previousNextCursor: previousCursor,
                  firstPageNextCursor: response.nextCursor,
                })
              : { items: itemsForQuery, nextCursor: response.nextCursor };
          const visible = withDeepLinkedConversation(
            applyConversationMutationOverlays(
              reconciled.items,
              mutationOverlaysRef.current,
              requestEpoch,
            ),
            selectedConversationIdRef.current,
            selectedSnapshotRef.current,
          ).filter((item) =>
            conversationMatchesActiveList(
              item,
              activeView,
              listStatus ?? "ALL",
              viewerMemberIdRef.current,
            ),
          );
          if (activeView !== "ALL") {
            const tailIds = new Set(retainedTailConversationIds(visible, itemsForQuery));
            pendingTailCheckRef.current = {
              orgId: currentOrgId,
              view: activeView,
              targets: changedIds.filter((id) => tailIds.has(id)).slice(0, 20),
            };
          } else {
            pendingTailCheckRef.current = null;
          }
          if (visible.length === 0) {
            const emptyKind = conversationListEmptyKind(currentQuery);
            if (emptyKind === "searchEmpty" && search) {
              return { kind: "searchEmpty", query: search };
            }
            if (emptyKind === "searchFilterEmpty" && search) {
              return { kind: "searchFilterEmpty", query: search };
            }
            if (emptyKind === "filterEmpty") return { kind: "filterEmpty" };
            return { kind: "empty" };
          }
          return {
            kind: "ready",
            items: visible,
            nextCursor: reconciled.nextCursor,
          };
        });
        for (const id of changedIds) {
          changedConversationIdsRef.current.delete(id);
        }
        listLoadedQueryKeyRef.current = queryKey;
      } catch (error) {
        if (
          isStaleConversationListResponse({
            aborted: controller.signal.aborted || orgIdRef.current !== currentOrgId,
            requestKey: queryKey,
            currentKey: listQueryKeyRef.current ?? queryKey,
          })
        ) {
          return;
        }
        if (mode === "merge") return;
        setListView({ kind: "error", message: toErrorMessage(error, unreachableMessageRef.current) });
        setListNextCursor(null);
      } finally {
        if (!controller.signal.aborted) {
          setListRefreshing(false);
          if (listInitialLoadQueryKeyRef.current === queryKey) {
            listInitialLoadQueryKeyRef.current = null;
          }
        }
      }
    })();

    return () => controller.abort();
  }, [
    organizationId,
    search,
    platform,
    unread,
    contactState,
    sort,
    view,
    listStatus,
    listReloadToken,
  ]);

  useEffect(() => {
    if (selectedConversationId == null) {
      setMessagesView({ kind: "idle" });
      setConversationDetails(null);
      setConversationEvents([]);
      setMessagesNextCursor(null);
      messagesLoadedKeyRef.current = null;
      return;
    }

    const controller = new AbortController();
    const currentOrgId = organizationId;
    const conversationId = selectedConversationId;
    const loadKey = `${currentOrgId}:${conversationId}`;
    const previous = messagesViewRef.current;
    const merge = previous.kind === "ready" && messagesLoadedKeyRef.current === loadKey;
    const requestEpoch = mutationEpochRef.current;

    if (!merge) {
      messagesGenerationRef.current += 1;
      messagesLoadOlderAbortRef.current?.abort();
      messagesLoadOlderAbortRef.current = null;
      messagesLoadOlderInFlightRef.current = false;
      setLoadingOlderMessages(false);
      setMessagesView({ kind: "loading" });
      setMessagesNextCursor(null);
      messagesNextCursorRef.current = null;
      setConversationEvents([]);
    }
    const generation = messagesGenerationRef.current;

    messagesFetchInFlightRef.current = true;

    void (async () => {
      try {
        const [messagesResponse, details] = await Promise.all([
          listConversationMessages(currentOrgId, conversationId, {
            limit: MESSAGE_PAGE_SIZE,
            signal: controller.signal,
          }),
          getConversation(currentOrgId, conversationId, controller.signal),
        ]);
        if (
          controller.signal.aborted ||
          orgIdRef.current !== currentOrgId ||
          selectedConversationIdRef.current !== conversationId ||
          messagesGenerationRef.current !== generation
        ) {
          return;
        }

        const pageEvents = messagesResponse.events ?? [];
        const overlaidDetails = applyConversationDetailsMutationOverlay(
          details,
          mutationOverlaysRef.current.get(conversationId),
          requestEpoch,
        );

        if (merge && previous.kind === "ready") {
          setMessagesView((current) => {
            const base = current.kind === "ready" ? current.items : previous.items;
            const items = mergeMessageFirstPage(base, messagesResponse.items);
            return items.length === 0 ? { kind: "empty" } : { kind: "ready", items };
          });
          setConversationEvents((current) => mergeTimelineEvents(current, pageEvents));
          setConversationDetails(overlaidDetails);
          setSelectedSnapshot((current) =>
            patchSelectedSnapshotFromDetails(current, overlaidDetails),
          );
          void markSelectedReadFromNewestPage(
            currentOrgId,
            conversationId,
            messagesResponse.items,
          );
          return;
        }

        const items = chronologicalMessages(messagesResponse.items);
        setMessagesView(items.length === 0 ? { kind: "empty" } : { kind: "ready", items });
        setConversationEvents(pageEvents);
        setMessagesNextCursor(messagesResponse.nextCursor);
        messagesNextCursorRef.current = messagesResponse.nextCursor;
        setConversationDetails(overlaidDetails);
        setSelectedSnapshot((current) =>
          patchSelectedSnapshotFromDetails(current, overlaidDetails),
        );
        messagesLoadedKeyRef.current = loadKey;
        void markSelectedReadFromNewestPage(
          currentOrgId,
          conversationId,
          messagesResponse.items,
        );
      } catch (error) {
        if (
          controller.signal.aborted ||
          orgIdRef.current !== currentOrgId ||
          selectedConversationIdRef.current !== conversationId
        ) {
          return;
        }
        if (merge) return;
        setMessagesView({ kind: "error", message: toErrorMessage(error, unreachableMessageRef.current) });
        setConversationDetails(null);
        setConversationEvents([]);
        setMessagesNextCursor(null);
      } finally {
        if (
          !controller.signal.aborted &&
          orgIdRef.current === currentOrgId &&
          selectedConversationIdRef.current === conversationId
        ) {
          messagesFetchInFlightRef.current = false;
        }
      }
    })();

    return () => {
      controller.abort();
      messagesFetchInFlightRef.current = false;
    };
  }, [organizationId, selectedConversationId, messagesReloadToken]);

  usePendingAttachmentReconciliation({
    selectedConversationId,
    messagesView,
    refreshMessages,
    messagesFetchInFlightRef,
  });

  const loadMoreConversations = useCallback((): boolean => {
    const cursor = listNextCursorRef.current;
    if (!cursor || listLoadMoreInFlightRef.current) return false;
    if (listViewRef.current.kind !== "ready") return false;

    const currentOrgId = orgIdRef.current;
    const currentQuery = { search, platform, unread, contactState, sort, view, status: listStatus };
    const queryKey = conversationListQueryKey(currentOrgId, currentQuery);
    const controller = new AbortController();
    listLoadMoreAbortRef.current?.abort();
    listLoadMoreAbortRef.current = controller;
    listLoadMoreInFlightRef.current = true;
    setLoadingMoreConversations(true);
    const requestEpoch = mutationEpochRef.current;

    void (async () => {
      try {
        const response = await listConversations(currentOrgId, {
          cursor,
          limit: CONVERSATION_PAGE_SIZE,
          search,
          platform,
          unread,
          contactState,
          sort,
          view,
          status: listStatus,
          signal: controller.signal,
        });
        if (
          isStaleConversationListResponse({
            aborted: controller.signal.aborted || orgIdRef.current !== currentOrgId,
            requestKey: queryKey,
            currentKey: conversationListQueryKey(orgIdRef.current, {
              search,
              platform,
              unread,
              contactState,
              sort,
              view,
              status: listStatus,
            }),
          })
        ) {
          return;
        }
        let itemsForQuery = response.items;
        if (
          unreadFilterActiveRef.current &&
          lastUnreadMutationReadConversationIdRef.current != null
        ) {
          const excludeId = lastUnreadMutationReadConversationIdRef.current;
          itemsForQuery = itemsForQuery.filter(
            (item) => item.conversationId !== excludeId,
          );
          lastUnreadMutationReadConversationIdRef.current = null;
        }
        const page = applyConversationMutationOverlays(
          itemsForQuery,
          mutationOverlaysRef.current,
          requestEpoch,
        );
        setListView((current) => {
          if (current.kind !== "ready" || current.nextCursor !== cursor) {
            return current;
          }
          return {
            kind: "ready",
            items: appendConversations(current.items, page),
            nextCursor: response.nextCursor,
          };
        });
      } catch (error) {
        if (controller.signal.aborted || orgIdRef.current !== currentOrgId) return;
        void error;
      } finally {
        if (listLoadMoreAbortRef.current === controller) {
          listLoadMoreAbortRef.current = null;
          listLoadMoreInFlightRef.current = false;
          setLoadingMoreConversations(false);
        }
      }
    })();

    return true;
  }, [search, platform, unread, contactState, sort, view, listStatus]);

  const ensureQuotedMessageLoaded = useCallback(async (messageId: string): Promise<boolean> => {
    const conversationId = selectedConversationIdRef.current;
    if (conversationId == null) {
      return false;
    }

    const hasMessage = (): boolean => {
      const view = messagesViewRef.current;
      return view.kind === "ready" && view.items.some((item) => item.messageId === messageId);
    };

    const initialPlan = quotedMessageSeekPlan({
      isLoaded: hasMessage(),
      hasOlderPages: messagesNextCursorRef.current != null,
    });
    if (initialPlan === "already-loaded") {
      return true;
    }
    if (initialPlan === "unavailable") {
      return false;
    }

    const currentOrgId = orgIdRef.current;
    let cursor = messagesNextCursorRef.current;
    let safety = 0;

    while (cursor && safety < 40) {
      const response = await listConversationMessages(currentOrgId, conversationId, {
        cursor,
        limit: MESSAGE_PAGE_SIZE,
      });
      if (
        orgIdRef.current !== currentOrgId ||
        selectedConversationIdRef.current !== conversationId
      ) {
        return false;
      }

      setMessagesView((current) => {
        if (current.kind !== "ready") return current;
        return {
          kind: "ready",
          items: prependOlderMessages(current.items, response.items),
        };
      });
      setMessagesNextCursor(response.nextCursor);
      messagesNextCursorRef.current = response.nextCursor;

      if (hasMessage()) {
        return true;
      }

      cursor = response.nextCursor;
      safety += 1;
      if (!cursor) {
        break;
      }
    }

    return hasMessage();
  }, []);

  const loadOlderMessages = useCallback((): boolean => {
    const cursor = messagesNextCursorRef.current;
    const conversationId = selectedConversationIdRef.current;
    if (!cursor || conversationId == null || messagesLoadOlderInFlightRef.current) return false;
    if (messagesViewRef.current.kind !== "ready") return false;

    const currentOrgId = orgIdRef.current;
    const controller = new AbortController();
    messagesLoadOlderAbortRef.current?.abort();
    messagesLoadOlderAbortRef.current = controller;
    messagesLoadOlderInFlightRef.current = true;
    setLoadingOlderMessages(true);
    const generation = messagesGenerationRef.current;

    void (async () => {
      try {
        const response = await listConversationMessages(currentOrgId, conversationId, {
          cursor,
          limit: MESSAGE_PAGE_SIZE,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          orgIdRef.current !== currentOrgId ||
          selectedConversationIdRef.current !== conversationId ||
          messagesGenerationRef.current !== generation ||
          messagesNextCursorRef.current !== cursor
        ) {
          return;
        }
        setMessagesView((current) => {
          if (current.kind !== "ready") return current;
          return {
            kind: "ready",
            items: prependOlderMessages(current.items, response.items),
          };
        });
        setConversationEvents((current) =>
          mergeTimelineEvents(current, response.events ?? []),
        );
        messagesNextCursorRef.current = response.nextCursor;
        setMessagesNextCursor(response.nextCursor);
      } catch (error) {
        if (
          controller.signal.aborted ||
          orgIdRef.current !== currentOrgId ||
          selectedConversationIdRef.current !== conversationId
        ) {
          return;
        }
        void error;
      } finally {
        if (messagesLoadOlderAbortRef.current === controller) {
          messagesLoadOlderAbortRef.current = null;
          messagesLoadOlderInFlightRef.current = false;
          setLoadingOlderMessages(false);
        }
      }
    })();

    return true;
  }, []);

  // Organization-scoped SSE via InboxRealtimeProvider (one EventSource per tab).
  useEffect(() => {
    let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
    let coalesceStartedAt: number | null = null;
    let listDirty = false;
    let selectedDirty = false;
    const boundOrgId = organizationId;

    const flush = (): void => {
      coalesceTimer = null;
      coalesceStartedAt = null;
      if (orgIdRef.current !== boundOrgId) {
        listDirty = false;
        selectedDirty = false;
        return;
      }
      if (listDirty) {
        listDirty = false;
        refreshList();
      }
      if (selectedDirty) {
        selectedDirty = false;
        refreshMessages();
      }
    };

    const schedule = (affectsSelected: boolean, conversationId: number): void => {
      changedConversationIdsRef.current.add(conversationId);
      listDirty = true;
      if (affectsSelected) {
        selectedDirty = true;
      }
      const decision = planInboxEventCoalesce({
        now: Date.now(),
        startedAt: coalesceStartedAt,
        trailingMs: EVENTS_COALESCE_MS,
        maxWaitMs: INBOX_EVENTS_COALESCE_MAX_MS,
      });
      coalesceStartedAt = decision.startedAt;
      if (decision.flushNow) {
        if (coalesceTimer) {
          clearTimeout(coalesceTimer);
          coalesceTimer = null;
        }
        flush();
        return;
      }
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      coalesceTimer = setTimeout(flush, decision.delayMs);
    };

    // Staging SSE recovery: coalesce list/message refresh after transport reopen.
    // Order 38 keeps InboxRealtimeProvider (one EventSource); reconnect maps to openKind=reconnect.
    const reconcileAfterTransportRecovery = (openKind: "initial" | "reconnect"): void => {
      if (orgIdRef.current !== boundOrgId) {
        return;
      }
      const currentQueryKey = listQueryKeyRef.current;
      const refreshListNow = shouldRefreshListAfterEventsOpen({
        openKind,
        initialLoadQueryKey: listInitialLoadQueryKeyRef.current,
        currentQueryKey,
      });
      // Coalesce so rapid reconnect + CONVERSATION_CHANGED do not stack REST calls.
      if (refreshListNow) {
        listDirty = true;
      }
      if (
        shouldRefreshMessagesAfterEventsOpen({
          selectedConversationId: selectedConversationIdRef.current,
        })
      ) {
        selectedDirty = true;
      }
      if (!listDirty && !selectedDirty) {
        return;
      }
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      coalesceTimer = setTimeout(flush, EVENTS_COALESCE_MS);
    };

    const unsubscribe = subscribeInboxRealtime((event) => {
      if (orgIdRef.current !== boundOrgId) {
        return;
      }
      if (event.organizationId !== boundOrgId) {
        return;
      }
      schedule(
        event.conversationId === selectedConversationIdRef.current,
        event.conversationId,
      );
    });

    const unsubscribeReconnect = subscribeReconnect(() => {
      reconcileAfterTransportRecovery("reconnect");
    });

    return () => {
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      unsubscribe();
      unsubscribeReconnect();
    };
  }, [organizationId, refreshList, refreshMessages, subscribeInboxRealtime, subscribeReconnect]);

  // Hidden tabs must not mark read; becoming visible reconciles list + selected thread.
  useEffect(() => {
    let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
    const boundOrgId = organizationId;
    const onVisibilityChange = (): void => {
      if (!shouldReconcileInboxOnVisibility({ visibilityState: document.visibilityState })) {
        return;
      }
      if (orgIdRef.current !== boundOrgId) {
        return;
      }
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        if (orgIdRef.current !== boundOrgId) {
          return;
        }
        if (document.visibilityState !== "visible") {
          return;
        }
        refreshList();
        if (selectedConversationIdRef.current != null) {
          refreshMessages();
        }
      }, VISIBILITY_RECONCILE_COALESCE_MS);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [organizationId, refreshList, refreshMessages]);

  const applyWorkflowDto = useCallback((dto: ConversationDto, unreadCount?: number) => {
    mutationEpochRef.current += 1;
    const previous = mutationOverlaysRef.current.get(dto.conversationId);
    mutationOverlaysRef.current.set(dto.conversationId, {
      epoch: mutationEpochRef.current,
      status: dto.status,
      assignedMemberId: dto.assignedMemberId,
      assignedMember: dto.assignedMember,
      ...(unreadCount !== undefined
        ? { unreadCount }
        : previous?.unreadCount !== undefined
          ? { unreadCount: previous.unreadCount }
          : {}),
    });
    setConversationDetails((current) =>
      current?.conversationId === dto.conversationId ? dto : current,
    );
    setSelectedSnapshot((current) => {
      if (!current || current.conversationId !== dto.conversationId) return current;
      const patched = patchSelectedSnapshotFromDetails(current, dto);
      if (!patched) return current;
      return unreadCount !== undefined ? { ...patched, unreadCount } : patched;
    });
    const activeView = viewRef.current ?? "ALL";
    setListView((current) => {
      if (current.kind !== "ready") return current;
      const patched = current.items.flatMap((item) => {
        if (item.conversationId !== dto.conversationId) return [item];
        const next = {
          ...item,
          status: dto.status,
          assignedMemberId: dto.assignedMemberId,
          assignedMember: dto.assignedMember,
          closedAt: dto.closedAt,
          ...(unreadCount !== undefined ? { unreadCount } : {}),
        };
        return conversationMatchesActiveList(
          next,
          activeView,
          listStatusRef.current ?? "ALL",
          viewerMemberIdRef.current,
        )
          ? [next]
          : [];
      });
      if (patched.length === 0) return { kind: "filterEmpty" };
      return { kind: "ready", items: patched, nextCursor: current.nextCursor };
    });
    refreshMessages();
  }, [refreshMessages]);

  const runWorkflow = useCallback(
    async (work: () => Promise<void>): Promise<{ ok: boolean; message?: string }> => {
      if (workflowPending) return { ok: false, message: i18n.t("inbox.workflow.errors.generic") };
      setWorkflowPending(true);
      try {
        await work();
        return { ok: true };
      } catch (error) {
        return {
          ok: false,
          message: inboxWorkflowErrorMessage(i18n.t.bind(i18n), error, unreachableMessageRef.current),
        };
      } finally {
        setWorkflowPending(false);
      }
    },
    [workflowPending],
  );

  const assignConversation = useCallback(
    async (conversationId: number, assignedMemberId: number | null) =>
      runWorkflow(async () => {
        const dto = await updateConversationAssignment(orgIdRef.current, conversationId, assignedMemberId);
        applyWorkflowDto(dto);
      }),
    [applyWorkflowDto, runWorkflow],
  );

  const setConversationStatus = useCallback(
    async (conversationId: number, status: "OPEN" | "CLOSED") =>
      runWorkflow(async () => {
        const dto = await updateConversationStatus(orgIdRef.current, conversationId, status);
        applyWorkflowDto(dto);
      }),
    [applyWorkflowDto, runWorkflow],
  );

  const markConversationUnreadAction = useCallback(
    async (conversationId: number) =>
      runWorkflow(async () => {
        if (selectedConversationIdRef.current === conversationId) {
          suppressMarkReadRef.current = conversationId;
        }
        try {
          const response = await markConversationUnread(orgIdRef.current, conversationId);
        const details = conversationDetails;
        if (details?.conversationId === conversationId) {
          applyWorkflowDto(details, response.unreadCount);
        } else {
          mutationEpochRef.current += 1;
          mutationOverlaysRef.current.set(conversationId, {
            epoch: mutationEpochRef.current,
            unreadCount: response.unreadCount,
          });
          setListView((current) => {
            if (current.kind !== "ready") return current;
            return {
              kind: "ready",
              nextCursor: current.nextCursor,
              items: current.items.map((item) =>
                item.conversationId === conversationId
                  ? { ...item, unreadCount: response.unreadCount }
                  : item,
              ),
            };
          });
          setSelectedSnapshot((current) =>
            patchSelectedSnapshotUnread(current, conversationId, response.unreadCount),
          );
        }
        } catch (error) {
          if (suppressMarkReadRef.current === conversationId) {
            suppressMarkReadRef.current = null;
          }
          throw error;
        }
      }),
    [applyWorkflowDto, conversationDetails, runWorkflow],
  );

  return {
    listView,
    messagesView,
    conversationEvents,
    selectedConversationId,
    selectedConversation,
    conversationDetails,
    contactActionStatus,
    sendStatus,
    hasMoreConversations: listNextCursor != null,
    loadingMoreConversations,
    listRefreshing,
    hasOlderMessages: messagesNextCursor != null,
    loadingOlderMessages,
    selectConversation,
    openConversationById,
    loadMoreConversations,
    loadOlderMessages,
    ensureQuotedMessageLoaded,
    refreshList,
    refreshMessages,
    saveContact,
    restoreContact,
    clearContactActionError,
    clearSendError,
    sendMessage,
    sendTemplateMessage,
    workflowPending,
    assignConversation,
    setConversationStatus,
    markConversationUnread: markConversationUnreadAction,
  };
}
